/**
 * Pick a gym from the shared list, or add one (B-52, D-103).
 *
 * Used by Profile and by owner onboarding. It does not write the choice anywhere —
 * `onPick` does — so onboarding can hold it until its single write at the end (D-90).
 * Adding a gym DOES write, to the shared list, because that is the only way the next
 * person at this PIN can find it.
 */

import { useEffect, useRef, useState } from "react";
import type { GymRef } from "@altrtrack/core";
import { isPinCode } from "@altrtrack/core";
import { findGyms } from "../data.js";
import { addGym } from "../write.js";

export function GymPicker({
  uid,
  onPick,
  initialPin = "",
  selected,
}: {
  uid: string;
  onPick: (gym: GymRef) => void;
  initialPin?: string;
  selected?: GymRef | undefined;
}) {
  const [pin, setPin] = useState(initialPin);
  const [gyms, setGyms] = useState<GymRef[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const trimmedPin = pin.trim();
  const validPin = isPinCode(trimmedPin);
  // The PIN on screen now, for an add() that resolves after the PIN was edited: its
  // result belongs to the PIN it was added at, not to whatever list is showing then.
  const pinNow = useRef(trimmedPin);
  pinNow.current = trimmedPin;

  useEffect(() => {
    setGyms(null);
    setError(null);
    setNote(null);
    if (!validPin) return;
    let live = true;
    findGyms(trimmedPin)
      .then((found) => live && setGyms(found))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [trimmedPin, validPin]);

  const add = async () => {
    const addedAt = trimmedPin;
    setAdding(true);
    setNote(null);
    const result = await addGym(uid, name, addedAt);
    setAdding(false);
    if (!result.ok) {
      setNote(result.detail);
      return;
    }
    if (result.existed) setNote(`${result.gym.name} is already listed — picked it.`);
    if (pinNow.current === addedAt) {
      setGyms((current) => {
        const without = (current ?? []).filter((g) => g.id !== result.gym.id);
        return [...without, result.gym].sort((a, b) => a.name.localeCompare(b.name));
      });
    }
    setName("");
    onPick(result.gym);
  };

  // A pick made at another PIN is still the pick; say so, since no row below shows it.
  const looking = validPin && gyms === null && !error;
  const showSelectedLine = selected !== undefined && !looking && !gyms?.some((g) => g.id === selected.id);

  return (
    <div className="gym-picker">
      <label className="field">
        <span className="label">Gym PIN code</span>
        <input
          className="input"
          value={pin}
          inputMode="numeric"
          autoComplete="postal-code"
          placeholder="560001"
          aria-invalid={trimmedPin.length > 0 && !validPin}
          onChange={(e) => setPin(e.target.value)}
        />
      </label>
      {trimmedPin.length > 0 && !validPin && <p className="sub field-error">A PIN code is 4 to 10 digits.</p>}

      {error && (
        <div className="notice bad">
          <b>Couldn't load gyms.</b>
          <p className="sub">{error}</p>
        </div>
      )}

      {validPin && gyms === null && !error && <p className="sub">Looking…</p>}

      {showSelectedLine && selected && (
        <p className="sub">
          Selected: {selected.name} · {selected.pinCode}
        </p>
      )}

      {gyms && (
        <>
          {gyms.length === 0 ? (
            <p className="sub">No gyms listed at {trimmedPin} yet.</p>
          ) : (
            <ul className="answers">
              {gyms.map((g) => {
                const picked = g.id === selected?.id;
                return (
                  <li key={g.id}>
                    <button
                      className={picked ? "answer-row gym-row gym-row-picked" : "answer-row gym-row"}
                      type="button"
                      aria-pressed={picked}
                      onClick={() => {
                        setNote(null);
                        onPick(g);
                      }}
                    >
                      <span>
                        {g.name} <span className="sub">{g.pinCode}</span>
                      </span>
                      {picked && <span className="gym-row-picked-tag">Selected</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="field-row">
            <label className="field gym-add">
              <span className="label">Not listed? Add it</span>
              <input
                className="input"
                value={name}
                placeholder="Gym name"
                autoComplete="organization"
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <button className="btn" type="button" disabled={adding || name.trim().length < 2} onClick={add}>
              {adding ? "Adding…" : "Add my gym"}
            </button>
          </div>
          <p className="sub">Anyone at this PIN will see the name you add, and it can't be changed later.</p>
        </>
      )}

      {note && <p className="sub">{note}</p>}
    </div>
  );
}
